import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";

// The spec read (0051 slice 3): GET /api/v1/products/{pid}/spec, what the
// spec column and its drawer read, and GET /api/v1/products/{pid}/nodes/{nid},
// one node on demand (an earlier version the drawer opens).

// The spec read's key sits under the product's tree key, so every write that
// invalidates ["product", pid, "tree"] (a save, an act, a pass's end, a rename,
// the page's step change) refreshes it with no extra wiring.
export function specQueryKey(productId: string) {
  return ["product", productId, "tree", "spec"] as const;
}

export function useSpec(productId: string, opts: { refetchOnWindowFocus?: boolean } = {}) {
  return useQuery({
    queryKey: specQueryKey(productId),
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET("/api/v1/products/{pid}/spec", {
        params: { path: { pid: productId } },
      });
      // The status rides the thrown error, so the app's retry rule reads it.
      if (error) throw Object.assign(new Error("spec"), { status: response.status });
      return data;
    },
    enabled: !!productId,
    // The next-step bar carries the live state; the spec refreshes on the
    // writes that change it, as the product page's tree read does. A page may
    // ask for a window-focus refetch (the Changes page).
    refetchOnWindowFocus: opts.refetchOnWindowFocus ?? false,
  });
}

// useSpecNode reads one node of the product's tree, only once a node is asked
// for (nodeId null reads nothing).
export function useSpecNode(productId: string, nodeId: string | null) {
  return useQuery({
    queryKey: ["product", productId, "tree", "node", nodeId] as const,
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET("/api/v1/products/{pid}/nodes/{nid}", {
        params: { path: { pid: productId, nid: nodeId ?? "" } },
      });
      if (error) throw Object.assign(new Error("node"), { status: response.status });
      return data;
    },
    enabled: !!productId && nodeId !== null,
  });
}
